import { useMemo } from "react";
import { ChipPicker, type ChipPickerChip, type PickerOption } from "@cntrl/ui";
import { useBandTeamOrNull, bandRoleLabel } from "../../app/hooks/useBandTeam.js";
import { useAssignableRows, useInvitableRows } from "../../app/hooks/useAssignable.js";
import { useMe } from "../../app/hooks/useSession.js";
import { toPersonPickerChip, toPersonPickerOption } from "../shared/PersonPickerOption.js";

/** The normalized candidate both sources map onto, so the render below never branches on where it came from. */
interface PickerRow {
  personId: string;
  displayName: string;
  avatarMediaId: string | null;
  jobLabel: string;
  /** Granted role key, so a full-access band member shows the AV mark beside the job (ART-171). */
  roleKey: string | null;
  isOwner: boolean;
  group?: string;
}

export interface CrewInviteePickerProps {
  /** Selected crew, by personId. Owned by the form so submit can send them with the create. */
  value: string[];
  onChange: (personIds: string[]) => void;
  /** Field label. Defaults to the opportunity voice. */
  label?: string;
  /** Placeholder on the type-ahead input. */
  searchPlaceholder?: string;
  /** Offer the CURRENT USER as a candidate. An opportunity's author is never their own invitee (they already
   *  see it by authorship), but a task can absolutely be assigned to yourself — so tasks opt in. */
  includeSelf?: boolean;
  /** Drop the OWNER seat. Per CLAUDE.md the artist IS the band: the owner's person record is NAMED the band,
   *  so it shows up in a person list as "Midnight Freight" rather than a human. It is not crew, so the task
   *  assignee list excludes it. The role can't tell you this — the owner seat still carries `band_member_av`
   *  — `isOwner` is the discriminator. */
  excludeOwner?: boolean;
  /**
   * Seats to leave OUT of the ADD list entirely — people already committed somewhere the chips cannot show.
   *
   * The picker's own rule is "already chosen leaves the list", and `value` is what it knows about. That is
   * the whole story for an opportunity (its chips ARE its invitees), but a show's members section stages
   * ADDITIONS while its roster lives in a list below, so the roster was invisible here: the picker offered
   * people already on the show and the caller filtered them back out in `onChange` — a pick that looked
   * accepted and did nothing.
   */
  exclude?: string[];
  /** Optional consequence line under the control, given the chosen count. Omit it for a field whose label
   *  already says everything (a task's "Assignees (Optional)"). */
  hint?: (count: number) => string;
  /**
   * WHERE the candidates come from. Default `"crew"` is the band team — right for a show's members and an
   * opportunity's invitees, both of which are band-side by definition.
   *
   * `"assignable"` is the TASK source: `GET .../tasks/assignable`, which is the assignment gate read
   * forwards. It spans band AND org seats and is narrowed by the caller's own role, so the list is exactly
   * what the save will accept. The crew source cannot express either half — an org staffer is not a band
   * seat, and it knows nothing about who is asking, so an intern was offered everyone and refused on save.
   *
   * `"invitable"` is the OPPORTUNITY source (ART-176): `GET .../opportunities/invitable`,
   * `core.invitable_people` — band crew ∪ the org (label/management/booking) seats reaching this band. It is
   * the assignment gate's candidate set WITHOUT the task-assign narrowing, because an invite is not an
   * assignment; the crew source could offer only band seats, so a firm's staff never appeared.
   */
  source?: "crew" | "assignable" | "invitable";
}

/**
 * The crew picker that rides an opportunity's CREATE (the legacy's "contacts autocomplete → invitee chips",
 * mapped onto our crew seats now that the contacts rolodex is retired), and the ASSIGNEE picker on the task
 * create modal (2026-08-21) — same contract (choose people off the band team), so it takes props for what
 * differs rather than being forked.
 *
 * For opportunities it is not a nicety: `opp_visibility` admits the creator and invitees ONLY, so an
 * opportunity created with an empty picker is visible to its author alone. The `hint` line says so rather
 * than letting someone discover it.
 *
 * The control is `@cntrl/ui`'s `ChipPicker` — the type-ahead ADD trigger over a chip list, which was
 * extracted FROM this file (2026-08-23) when the release link picker turned out to want the same shell. What
 * stays here is the only part that was ever crew-shaped: the seat query, the candidate filter and the
 * listbox row (avatar · name · role).
 *
 * Reads the band's seat list, which widened to `band:read` with the TM ruling (2026-08-20), so every seat that
 * can create an opportunity can also address one.
 */
export function CrewInviteePicker({
  value,
  onChange,
  label = "Invite crew",
  searchPlaceholder = "Search your crew…",
  includeSelf = false,
  excludeOwner = false,
  exclude,
  hint,
  source = "crew",
}: CrewInviteePickerProps) {
  const assignable = source === "assignable";
  const invitable = source === "invitable";
  // Only the chosen source is FETCHED — `enabled` keeps the unused ones from firing a request the caller has
  // no use for, and keeps a task modal from 404-ing on a band-team read it never wanted.
  //
  // **`useBandTeamOrNull`, not `useBandTeam`, and `enabled` was NOT enough.** That one reads the THROWING
  // band context, and a hook throws whether or not its query is disabled — so the messaging source this picker
  // once had (the inbox's, since moved to `MessageablePicker`) took the whole dialog down with
  // "useBandContext() requires a mounted <BandProvider>" wherever no band was mounted — the inbox opens from
  // the TOPBAR, outside every shell's own provider. It is the CrewProfileModal rule verbatim —
  // never add a hook using the throwing context to something that can mount without a band — and the
  // context-safe sibling already existed for exactly this.
  //
  // No test could see it: `renderWithBand` mounts a provider, so every suite ran inside one.
  const team = useBandTeamOrNull(!assignable && !invitable);
  const candidates = useAssignableRows({ enabled: assignable });
  const invitableRows = useInvitableRows({ enabled: invitable });
  const myPersonId = useMe().data?.person?.personId;

  // One row shape for both sources, so everything below is source-agnostic. The assignable source arrives
  // already resolved (grouped by audience, its job label taken from the TITLE) because the drawer's picker
  // lists the same set and the two must not describe one person differently; the crew source carries no
  // title, so its label falls back to the role.
  const rows = useMemo<PickerRow[]>(() => {
    if (assignable) return candidates.rows; // AssignableRow already carries roleKey
    if (invitable) return invitableRows.rows; // same normalized row shape (band ∪ reaching org seats)
    return team.items.map((s) => ({
      personId: s.personId!,
      displayName: s.displayName ?? "Member",
      avatarMediaId: s.avatarMediaId ?? null,
      jobLabel: s.role ? bandRoleLabel(s.role) : "",
      roleKey: s.role ?? null,
      isOwner: s.isOwner ?? false,
      group: undefined,
    }));
  }, [assignable, invitable, candidates.rows, invitableRows.rows, team.items]);

  // The creator is not a candidate by default: they already see the opportunity by authorship, and an invite
  // to yourself would sit in your own feed asking you to RSVP to your own plan. `includeSelf` opts back in for
  // callers where self-selection is meaningful (assigning yourself a task).
  //
  // ⚠️ SELF SURVIVES `excludeOwner`. That filter exists because the owner's person record is NAMED the band,
  // so it reads as "Midnight Freight" in a list of people — an argument about handing work to somebody ELSE,
  // which does not apply to your own row. Dropping it there would leave a band OWNER with no way to put a
  // task in their own to-do at all, which is what the drawer's "Assign to me" button was quietly covering
  // for (ART-121); self-assignment is always allowed by the gate itself.
  const seats = useMemo(
    () =>
      rows.filter(
        (s) =>
          s.personId &&
          (includeSelf || s.personId !== myPersonId) &&
          (!excludeOwner || !s.isOwner || s.personId === myPersonId),
      ),
    [rows, myPersonId, includeSelf, excludeOwner],
  );
  const chosen = useMemo(
    () => value.map((id) => seats.find((s) => s.personId === id)).filter(Boolean),
    [value, seats],
  );

  // Already-chosen seats leave the list rather than sitting there inert — the chips are the selection.
  const options: PickerOption[] = useMemo(
    () =>
      seats
        .filter((s) => !value.includes(s.personId!) && !(exclude ?? []).includes(s.personId!))
        .map((s) => toPersonPickerOption(s)),
    [seats, value, exclude],
  );

  // The chips carry the seat's avatar + FIRST name (ART-176); `label` stays the full name so the remove
  // button and AT still announce who is being dropped. The ADD list is the same rows minus what is chosen.
  const chips: ChipPickerChip[] = useMemo(
    () =>
      chosen.map((seat) => toPersonPickerChip(seat!)),
    [chosen],
  );

  return (
    <ChipPicker
      label={label}
      placeholder={searchPlaceholder}
      // Read the source that is actually FETCHING. This was `team.isLoading` for both, and the crew query is
      // disabled on the assignable source — a disabled query never reports loading — so that picker had no
      // loading state at all: it mounted an empty list reading "No matches" and filled a tick later.
      loading={
        assignable ? candidates.isLoading
        : invitable ? invitableRows.isLoading
        : team.isLoading
      }
      loadingLabel={assignable || invitable ? "Loading people…" : "Loading crew…"}
      options={options}
      chips={chips}
      onAdd={(personId) => onChange([...value, personId])}
      onRemove={(personId) => onChange(value.filter((id) => id !== personId))}
      hint={hint ? hint(value.length) : undefined}
    />
  );
}
